import React from 'react';
import {
    SidePanelBodyAttribute,
    SidePanelBodyAttributeBody,
    SidePanelBodyAttributeTitle,
} from 'src/registry/components/sidePanel/SidePanelBody';
import { visitorMessages } from 'src/features/visitor/utils/messages';
import { getFormattedDateTimeForDetailPanel } from 'src/utils/getFormattedDateTime';
import { useIntl } from 'react-intl';
import { ActiveStayPeriod } from 'src/features/stay/types';
import CellStayPeriodZones from 'src/features/stay/components/cell/CellStayPeriodZones';
import SidePanelAttribute from 'src/features/stay/components/sidePanel/SidePanelAttribute';
import { CURRENCY } from 'src/types';
import { useGetStayDuration } from 'src/hooks/useGetStayDuration';
import useHasPermissions from 'src/hooks/useHasPermissions';
import { PERMISSION } from 'src/features/auth';
import { PATH } from 'src/features/navigation/utils/constants';

type OverviewStayPeriodsProps = {
    identifiers: string;
    stayData: ActiveStayPeriod;
};

const OverviewStayPeriod = ({ stayData, identifiers }: OverviewStayPeriodsProps) => {
    const { formatMessage, locale, formatNumber } = useIntl();
    const { getDurationFromStartDateToEndDate } = useGetStayDuration();
    const accessPricingPlans = useHasPermissions([PERMISSION.ACCESS_PRICING_PLANS]);

    const pricingPlanName = stayData?.pricing_plan?.translations?.find((zone) => zone.language === locale)?.name ?? '-';
    const pricingPlanId = stayData?.pricing_plan?.id;
    const dueAmount = React.useMemo(() => {
        const amount = stayData?.due_amount_with_tax ?? 0;
        const currency = stayData?.pricing_plan?.currency ?? CURRENCY.BGN;

        return formatNumber(amount / 100, {
            style: 'currency',
            currency,
        });
    }, [formatNumber, stayData?.due_amount_with_tax, stayData?.pricing_plan?.currency]);

    return (
        <div className="mt-4 grid gap-4 sm:gap-6">
            <SidePanelBodyAttribute>
                <SidePanelBodyAttributeTitle>{formatMessage(visitorMessages.identifiers)}</SidePanelBodyAttributeTitle>
                <SidePanelBodyAttributeBody control={identifiers} fallbackMessage="-">
                    {identifiers}
                </SidePanelBodyAttributeBody>
            </SidePanelBodyAttribute>

            <SidePanelAttribute
                label={formatMessage(visitorMessages.start)}
                value={getFormattedDateTimeForDetailPanel(stayData?.start)}
                className="flex flex-col gap-0"
            />

            <SidePanelAttribute
                label={formatMessage(visitorMessages.end)}
                value={getFormattedDateTimeForDetailPanel(stayData?.end ?? undefined)}
                className="flex flex-col gap-0"
            />

            <SidePanelAttribute
                label={formatMessage(visitorMessages.duration)}
                value={getDurationFromStartDateToEndDate({
                    startDate: stayData?.start,
                    endDate: stayData?.end,
                })}
                className="flex flex-col gap-0"
            />

            <SidePanelBodyAttribute>
                <SidePanelBodyAttributeTitle>{formatMessage(visitorMessages.zones)}</SidePanelBodyAttributeTitle>
                <SidePanelBodyAttributeBody control={stayData?.zones} fallbackMessage="-">
                    {stayData?.zones?.length > 0 ? (
                        <CellStayPeriodZones zonesData={stayData?.zones} className="font-normal" />
                    ) : (
                        '-'
                    )}
                </SidePanelBodyAttributeBody>
            </SidePanelBodyAttribute>

            {/*TODO: update with real data once we have it*/}
            <SidePanelAttribute
                label={formatMessage(visitorMessages.access)}
                value={'-'}
                className="flex flex-col gap-0"
            />

            <SidePanelAttribute
                label={formatMessage(visitorMessages.pricingPlan)}
                value={pricingPlanName}
                link={
                    accessPricingPlans && pricingPlanId
                        ? `/${PATH.ADMINISTRATION}/pricing-plan/${pricingPlanId}`
                        : undefined
                }
                className="flex flex-col gap-0"
            />

            <SidePanelAttribute
                label={formatMessage(visitorMessages.amountDue)}
                value={dueAmount}
                className="flex flex-col gap-0"
            />
        </div>
    );
};

export default OverviewStayPeriod;
